/**
 * A chart of scenarios to the minimum percentage down required
 */
export const MIN_DOWN_PAYMENT_SIZES = {
  veterans: 0.0, // Any borrower is eligible for veteran's benefits
  fthb: 0.03, // Any borrower is a first-Time Home Buyer
  oneUnitPrimary: 0.05, // Property is a single family, condo, or townhouse used a primary residence
  secondaryHome: 0.1, // Property is used as a secondary home
  multiFamilyPrimary: 0.15, // Property is a multi-family home used as a primary residence
  oneUnitInvestment: 0.15, // Property is a single family, condo, or townhouse used an investment property
  multiFamilyInvestment: 0.25 // Property is a multi-family home used an investment property
};

/**
 * Returns true if status is set and indicates veteran benefit eligibility
 *
 * @param {*} status - Veteran status
 */
export const isVeteranEligible = (status) =>
  status && ['Eligible', 'SurvivingSpouse'].indexOf(status) > -1;

export const getBorrowerRuleset = (borrower, coborrower) => {
  // Check if either borrower is eligible for veteran's benefits
  if (
    isVeteranEligible(borrower.get('vaQualification')) ||
    (coborrower && isVeteranEligible(coborrower.get('vaQualification')))
  ) {
    // Follow the Veteran ruleset
    return 'veterans';
  }
  // Check either borrower is a first-time home buyer
  if (borrower.get('firstTimeBuyer') || (coborrower && coborrower.get('firstTimeBuyer'))) {
    // Follow the FTHB ruleset
    return 'fthb';
  }
  // No ruleset applies to this borrower
  return null;
};

/**
 * Returns true if the property is a multi-family home
 *
 * @param {*} propertyType
 */
export const isMultiUnitProperty = (propertyType) => propertyType === 'MultiFamily';

export const getLoanRuleset = (loan) => {
  // Rules are all based on the occupancy and property types
  const occupancyType = loan.get('occupancy');
  if (occupancyType === 'PrimaryResidence') {
    // Primary residencies
    const propertyType = loan.get('propertyType');
    if (isMultiUnitProperty(propertyType)) {
      // Follow multi-family primary residency ruleset
      return 'multiFamilyPrimary';
    }
    // Follow one unit (single-family, condo, townhouse) primary residency ruleset
    return 'oneUnitPrimary';
  } else if (occupancyType === 'SecondHome') {
    // Follow secondary residency ruleset
    return 'secondaryHome';
  } else if (occupancyType === 'InvestmentProperty') {
    // Investment properties
    const propertyType = loan.get('propertyType');
    if (isMultiUnitProperty(propertyType)) {
      // Follow multi-family investment property ruleset
      return 'multiFamilyInvestment';
    }
    // Follow one unit (single-family, condo, townhouse) investment property ruleset
    return 'oneUnitInvestment';
  }
  // All other scenarios do not apply a ruleset
  return null;
};

/**
 * Determine what ruleset this scenario should follow
 *
 * Always returns null if this is an advisor link
 *
 * @param {*} model - Current session model
 * @param getBorrowerRulesetFunc - borrower ruleset function -- injected for testing
 * @param getLoanRulesetFunc - loan ruleset function -- injected for testing
 */
export const getDownPaymentRuleset = (
  model,
  getBorrowerRulesetFunc = getBorrowerRuleset,
  getLoanRulesetFunc = getLoanRuleset
) => {
  // Check if this is an advisor link
  if (!model.getIn(['loan', 'advisor'])) {
    // Check borrowers, then loan if nothing came up
    return (
      getBorrowerRulesetFunc(
        model.get('borrower'),
        model.getIn(['loan', 'hasCoborrower']) ? model.get('coborrower') : undefined
      ) || getLoanRulesetFunc(model.get('loan'))
    );
  }

  return null;
};

/**
 * Determines if you fall within a particular down payment ruleset
 *   and if you meet its requirements.
 *
 * If not, returns the name of the ruleset so we can redirect
 *   you to its hard-stop card.
 *
 * @param {*} model - Current session model
 * @param getDownPaymentRulesetFunc - ruleset function -- injected for testing
 */
export const evaluateDownPayment = (model, getDownPaymentRulesetFunc = getDownPaymentRuleset) => {
  // Figure out what down payment ruleset this scenario entails
  const ruleset = getDownPaymentRulesetFunc(model);
  if (ruleset) {
    // Check if there's a minimum rule.
    const minimum = MIN_DOWN_PAYMENT_SIZES[ruleset];
    if (minimum !== undefined) {
      const downPayment = model.getIn(['loan', 'downPayment']) || 0;
      const loanAmount = model.getIn(['loan', 'purchasePrice']) || 0;
      if (downPayment < Math.floor(loanAmount * minimum)) {
        return ruleset;
      }
    }
  }
  return null;
};



// WEBPACK FOOTER //
// ./src/modules/down-payment-evaluation.js